/**
 * `<ContactRunsTab>` — paginated list of runs for a contact. Lazy-loaded
 * on tab activation per NFR-PERF-10.
 */

import { Badge, TableBody, TableHeader } from "@cometchat-ai/ui-dashboard";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useWorkspace } from "@cometchat-ai/auth/client";

import { CursorPaginationBar, useCursorPager } from "../cursor-pagination";
import { DataGrid, DataGridCell, DataGridHead, DataGridHeaderRow, DataGridRow } from "../data-grid";
import { ErrorState } from "../error-state";
import { ListSkeleton } from "../list-skeleton";
import { StatusPill } from "../status-pill";
import { useContactsClient } from "../../lib/api/use-contacts-client";
import type { Paginated, RunSummary } from "../../lib/api/types";
import { relativeTime } from "../../lib/relative-time";

export interface ContactRunsTabProps {
  contactId: string;
}

function RunStatusBadge({ status }: { status: string }) {
  const tone = status === "completed" ? "success" : status === "failed" ? "danger" : "neutral";
  return (
    <StatusPill tone={tone} className="font-normal capitalize">
      {status}
    </StatusPill>
  );
}

export function ContactRunsTab({ contactId }: ContactRunsTabProps) {
  const { t } = useTranslation("contacts");
  const client = useContactsClient();
  const navigate = useNavigate();
  const { workspace } = useWorkspace();

  const pager = useCursorPager();
  const query = useQuery({
    queryKey: ["contacts", "runs", contactId, pager.cursor] as const,
    queryFn: () => client.runs(contactId, { cursor: pager.cursor, limit: 10 }),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  if (query.isPending) {
    return (
      <ListSkeleton
        rows={5}
        itemClassName="h-8 w-full"
        className="space-y-2 rounded-xl border border-border p-4"
        data-testid="runs-skeleton"
      />
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        message={t("detail.runs.error")}
        onRetry={query.refetch}
        data-testid="runs-error"
      />
    );
  }

  const runs: RunSummary[] = query.data?.items ?? [];
  const canNext = Boolean((query.data as Paginated<RunSummary> | undefined)?.nextCursor);

  if (runs.length === 0) {
    return (
      <div className="rounded-xl border border-border p-4">
        <p className="text-sm text-muted-foreground" data-testid="runs-empty">
          {t("detail.runs.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="-mx-5 border-y border-border">
        <DataGrid data-testid="runs-table" className="border-b-0">
          <TableHeader>
            <DataGridHeaderRow>
              <DataGridHead className="w-[33%] pl-5">{t("detail.runs.started")}</DataGridHead>
              <DataGridHead className="w-[33%]">{t("detail.runs.status")}</DataGridHead>
              <DataGridHead className="w-[34%] pr-5" divider={false}>
                {t("detail.runs.agent")}
              </DataGridHead>
            </DataGridHeaderRow>
          </TableHeader>
          <TableBody>
            {runs.map((r) => (
              <DataGridRow
                key={r.id}
                className="cursor-pointer last:border-0"
                onClick={() =>
                  navigate(`/w/${workspace.slug}/conversations?c=${r.sessionId}&run=${r.id}`)
                }
                data-testid={`run-row-${r.id}`}
              >
                <DataGridCell className="pl-5">{relativeTime(r.startedAt)}</DataGridCell>
                <DataGridCell>
                  <RunStatusBadge status={r.status} />
                </DataGridCell>
                <DataGridCell className="pr-5" divider={false}>
                  <Badge
                    variant="secondary"
                    className="max-w-full truncate align-middle font-normal"
                  >
                    {r.entryAgentId}
                  </Badge>
                </DataGridCell>
              </DataGridRow>
            ))}
          </TableBody>
        </DataGrid>
      </div>
      <CursorPaginationBar
        data-testid="runs-pagination"
        pageIndex={pager.pageIndex}
        canPrev={pager.canPrev}
        canNext={canNext}
        busy={query.isFetching}
        onPrev={pager.toPrev}
        onNext={() => pager.toNext(query.data?.nextCursor)}
      />
    </div>
  );
}
